import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { messagesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { EnvelopeSimple, PaperPlaneTilt, Eye, Megaphone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Messages = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [messageDetailOpen, setMessageDetailOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '',
    content: ''
  });

  useEffect(() => {
    loadMessages();
  }, [filterStatus]);

  const loadMessages = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const { data } = await messagesApi.getAll(params);
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    try {
      await messagesApi.broadcast(broadcastForm);
      toast.success(language === 'fr' ? 'Message envoyé à tous les parents' : 'Message sent to all parents');
      setBroadcastDialogOpen(false);
      setBroadcastForm({ subject: '', content: '' });
      loadMessages();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleViewMessage = async (message) => {
    try {
      const { data } = await messagesApi.getMessageDetail(message.id);
      setSelectedMessage(data);
      setMessageDetailOpen(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    try {
      await messagesApi.replyMessage(selectedMessage.id, { content: replyContent });
      toast.success(language === 'fr' ? 'Réponse envoyée' : 'Reply sent');
      setReplyContent('');
      const { data } = await messagesApi.getMessageDetail(selectedMessage.id);
      setSelectedMessage(data);
      loadMessages();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('messages')}</h1>
          {unreadCount > 0 && (
            <p className="font-manrope text-sm text-[#64748B]">
              {unreadCount} {language === 'fr' ? 'message(s) non lu(s)' : 'unread message(s)'}
            </p>
          )}
        </div>
        <Button
          onClick={() => setBroadcastDialogOpen(true)}
          className="bg-[#0052FF] hover:bg-[#0043D1]"
          data-testid="broadcast-btn"
        >
          <Megaphone size={18} className="mr-2" />
          {language === 'fr' ? 'Message à tous les parents' : 'Message to all parents'}
        </Button>
      </div>

      {/* Filter */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px] border-[#E2E8F0]" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              <SelectItem value="unread">{t('unread')}</SelectItem>
              <SelectItem value="read">{t('read')}</SelectItem>
              <SelectItem value="replied">{t('replied')}</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Messages Table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold">{t('date')}</TableHead>
                <TableHead className="font-outfit font-semibold">{t('from')}</TableHead>
                <TableHead className="font-outfit font-semibold">{t('subject')}</TableHead>
                <TableHead className="font-outfit font-semibold">Status</TableHead>
                <TableHead className="font-outfit font-semibold text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <TableRow 
                    key={message.id} 
                    className={`border-[#E2E8F0] hover:bg-[#F1F5F9] ${message.status === 'unread' ? 'bg-blue-50' : ''}`}
                  >
                    <TableCell className="font-manrope">{message.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="font-manrope">
                      <div>
                        <span className="font-medium">{message.sender_name}</span>
                        <span className="text-xs text-[#64748B] ml-2">
                          ({message.sender_role === 'parent' ? 'Parent' : message.sender_role})
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-manrope font-medium">{message.subject}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${message.status === 'unread' ? 'bg-yellow-100 text-yellow-700' :
                          message.status === 'read' ? 'bg-blue-100 text-blue-700' :
                          message.status === 'sent' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'}`}
                      >
                        {t(message.status) || message.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewMessage(message)}
                        className="hover:bg-[#EFF6FF] text-[#0052FF]"
                      >
                        <Eye size={18} className="mr-1" />
                        {t('viewDetails')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastDialogOpen} onOpenChange={setBroadcastDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Megaphone size={24} className="text-[#0052FF]" />
              {language === 'fr' ? 'Message à tous les parents' : 'Message to all parents'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBroadcast} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{t('subject')}</Label>
              <Input
                value={broadcastForm.subject}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, subject: e.target.value })}
                placeholder={language === 'fr' ? 'Objet du message' : 'Message subject'}
                required
                className="border-[#E2E8F0]"
                data-testid="broadcast-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('message')}</Label>
              <Textarea
                value={broadcastForm.content}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, content: e.target.value })}
                placeholder={language === 'fr' ? 'Votre message...' : 'Your message...'}
                required
                rows={6}
                className="border-[#E2E8F0]"
                data-testid="broadcast-content"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBroadcastDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="send-broadcast-btn">
                <PaperPlaneTilt size={18} className="mr-2" />
                {t('sendMessage')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog open={messageDetailOpen} onOpenChange={setMessageDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{selectedMessage?.subject}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="text-sm text-[#64748B] font-manrope">
                {t('from')}: {selectedMessage.sender_name} ({selectedMessage.sender_role}) • {selectedMessage.created_at?.slice(0, 16).replace('T', ' ')}
              </div>
              
              <div className="p-4 bg-[#F8FAFC] rounded-lg">
                <p className="font-manrope whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>
              
              {/* Replies */}
              {selectedMessage.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-outfit font-semibold">{language === 'fr' ? 'Réponses' : 'Replies'}</h4>
                  {selectedMessage.replies.map((reply, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${reply.sender_role !== 'parent' ? 'bg-[#0052FF]/10 ml-4' : 'bg-[#F1F5F9] mr-4'}`}>
                      <div className="text-xs text-[#64748B] mb-1">
                        {reply.sender_name} ({reply.sender_role}) • {reply.created_at?.slice(0, 16).replace('T', ' ')}
                      </div>
                      <p className="font-manrope text-sm">{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Form (only for messages from parents) */}
              {selectedMessage.sender_role === 'parent' && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="font-manrope">{t('reply')}</Label>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder={language === 'fr' ? 'Votre réponse...' : 'Your reply...'}
                    rows={3}
                    className="border-[#E2E8F0]"
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleReply} className="bg-[#0052FF] hover:bg-[#0043D1]">
                      <PaperPlaneTilt size={18} className="mr-2" />
                      {t('reply')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
